import React, { useCallback, useEffect, useState } from 'react';
import { Container, Row, Col, Nav, Form, Card, Button, InputGroup, Table } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { FaRegEnvelope } from "react-icons/fa";
import AddressForm from '../../components/Form/AddressForm';
import TermsModal from '../../components/TermsModal';
import PhoneInput from '../../components/Form/PhoneInput';
import API from '../../api/axios';
import { useNavigate, useParams } from 'react-router-dom';
import { buildSuccessAlert, buildErrorAlert, getApiErrorMessage } from '../../utils/alert';
import StatusAlert from '../../components/StatusAlert';
import { useSelector } from 'react-redux';
import {
    selectAuthUser,
} from '../../features/auth/authSlice';

// 基础信息验证
const basicSchema = yup.object().shape({
    supplier: yup.object().shape({
        // legalName: yup.string().required('Required'),
        // comercialName: yup.string().required('Required'),
        // businessIdentificationNumber: yup.string().required('Required'),
        // street: yup.string().required('Required'),
        // country: yup.string().required('Required'),
        // province: yup.string().required('Required'),
        // city: yup.string().required('Required'),
        // prefix: yup.string().required('Required'),
        // phone: yup.string().required('Required'),
        // companyEmail: yup.string().email('Invalid email').required('Required'),
    }),
});

// 合同条款验证
const contractSchema = yup.object().shape({
    contract: yup.object().shape({
        // prepayment: yup.string().required('Required'),
        // commission: yup.string().required('Required'),
        // cancel7: yup.string().required('Required'),
        // cancel15: yup.string().required('Required'),
        // cancel30: yup.string().required('Required'),
        // cancel60: yup.string().required('Required'),
    }),
});

// 银行信息验证
const bankSchema = yup.object().shape({
    bank: yup.object().shape({
        // accountHolder: yup.string().required('Required'),
        // swift: yup.string().required('Required'),
        // iban: yup.string().required('Required'),
    }),
});

const TABS = [
    { id: 'basic', label: 'Basic Information' },
    { id: 'contract', label: 'Contract Conditions' },
    { id: 'bank', label: 'Bank Details' },
];

const SCHEMA_MAP = {
    basic: basicSchema,
    contract: contractSchema,
    bank: bankSchema,
};

const withPrepaymentType = (data) => {
    const safe = {
        supplier: {},
        rentalDetail: {},
        bank: {},
        agreeTerms: false,
        ...data,
    };

    const pre = Number(safe.rentalDetail?.prepayment ?? 0);

    return {
        ...safe,
        rentalDetail: {
            ...safe.rentalDetail,
            prepayment: Number(safe.rentalDetail?.prepayment ?? 0),
            prepaymentType: pre === 0 ? 'only' : 'both',
            isTaxable: Boolean(safe.rentalDetail?.isTaxable ?? false),
            pricingMode: safe.rentalDetail?.pricingMode ?? 'FIXED',
        },
        agreeTerms: Boolean(safe.agreeTerms),
    };
};

const CompanyForm = () => {
    const user = useSelector(selectAuthUser);
    const role = user?.role;

    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    const [activeTab, setActiveTab] = useState('basic');
    const [showTerms, setShowTerms] = useState(false);
    const [loading, setLoading] = useState(isEditMode);
    const [initialValues, setInitialValues] = useState(null);
    const [selectOptions, setSelectOptions] = useState(null);
    const [submitAlert, setSubmitAlert] = useState(null);

    const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);

    const handleTabChange = useCallback(
        async (tabId, formik) => {
            try {
                const schema = SCHEMA_MAP[activeTab];

                await schema.validate(formik.values, {
                    abortEarly: false,
                });

                const newErrors = { ...formik.errors };

                Object.keys(newErrors).forEach((key) => {
                    if (key.startsWith(`${activeTab}.`)) {
                        delete newErrors[key];
                    }
                });

                formik.setErrors(newErrors);
                setActiveTab(tabId);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (err) {
                const errors = (err?.inner || []).reduce((acc, validationError) => {
                    if (validationError.path) {
                        acc[validationError.path] = validationError.message;
                    }
                    return acc;
                }, {});

                formik.setErrors({
                    ...formik.errors,
                    ...errors,
                });
            }
        },
        [activeTab]
    );

    const handleSubmit = useCallback(
        async (values, formikHelpers) => {
            setSubmitAlert(null);

            const payload = {
                supplier: {
                    companyName: values.supplier.companyName,
                    language: values.supplier.language,
                    currency: values.supplier.currency,
                    distanceUnit: values.supplier.distanceUnit,
                    isActive: values.supplier.isActive,
                    isReseller: values.supplier.isReseller,
                },
                rentalDetail: values.rentalDetail && {
                    prepayment: Number(values.rentalDetail.prepayment || 0),
                    commission: Number(values.rentalDetail.commission || 0),
                    durationType: values.rentalDetail.durationType,
                    cancellation7DaysRefund: Number(values.rentalDetail.cancellation7DaysRefund || 0),
                    cancellation15DaysRefund: Number(values.rentalDetail.cancellation15DaysRefund || 0),
                    cancellation30DaysRefund: Number(values.rentalDetail.cancellation30DaysRefund || 0),
                    cancellation60DaysRefund: Number(values.rentalDetail.cancellation60DaysRefund || 0),
                    chargeCancellationFee7Days: Boolean(values.rentalDetail.chargeCancellationFee7Days),
                    chargeCancellationFee15Days: Boolean(values.rentalDetail.chargeCancellationFee15Days),
                    chargeCancellationFee30Days: Boolean(values.rentalDetail.chargeCancellationFee30Days),
                    chargeCancellationFee60Days: Boolean(values.rentalDetail.chargeCancellationFee60Days),
                    isDiscountRejected: values.rentalDetail.isDiscountRejected,
                    api: values.rentalDetail.api,
                    isTaxable: Boolean(values.rentalDetail.isTaxable),
                    pricingMode: values.rentalDetail.pricingMode || 'FIXED',
                },
                invoiceInfo: values.invoiceInfo && {
                    legalName: values.invoiceInfo.legalName,
                    email: values.invoiceInfo.email,
                    prefix: values.invoiceInfo.prefix,
                    businessIdentNum: values.invoiceInfo.businessIdentNum,
                    webSite: values.invoiceInfo.webSite,
                    phone: values.invoiceInfo.phone,
                    country: values.invoiceInfo.country,
                    state: values.invoiceInfo.state,
                    city: values.invoiceInfo.city,
                    street: values.invoiceInfo.street,
                    houseNumber: values.invoiceInfo.houseNumber,
                    accountHolderName: values.invoiceInfo.accountHolderName,
                    iban: values.invoiceInfo.iban,
                    swift: values.invoiceInfo.swift,
                },
            };

            try {
                let savedId = id;
                const companyName =
                    values.supplier.companyName ||
                    values.invoiceInfo.legalName ||
                    '-';

                if (isEditMode) {
                    await API.patch(`/supplier/${id}`, payload);
                } else {
                    const response = await API.post('/supplier', payload);
                    savedId =
                        response?.data?.data?.id ||
                        response?.data?.id ||
                        '';
                }

                const successAlert = buildSuccessAlert(
                    isEditMode
                        ? 'Company updated successfully.'
                        : 'Company created successfully.',
                    {
                        details: [
                            `Company: ${companyName}`,
                            ...(savedId ? [`ID: ${savedId}`] : []),
                        ],
                    }
                );

                if (role === 'ADMIN') {
                    navigate('/companies/list', {
                        replace: true,
                        state: {
                            alert: successAlert,
                        },
                    });
                    return;
                }

                setSubmitAlert(successAlert);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                setSubmitAlert(
                    buildErrorAlert(
                        getApiErrorMessage(
                            error,
                            'Failed to save company information.'
                        )
                    )
                );

                window.scrollTo({ top: 0, behavior: 'smooth' });
            } finally {
                formikHelpers?.setSubmitting(false);
            }
        },
        [id, isEditMode, navigate, role]
    );

    const fetchCompanyForm = useCallback(async () => {
        if (isEditMode) {
            const response = await API.get(`/supplier/${id}/form`);

            return {
                initialValues: withPrepaymentType(response.data.data),
                selectOptions: response.data.options || {},
            };
        }

        const response = await API.get('/supplier/form-meta');

        return {
            initialValues: withPrepaymentType(response.data.defaults),
            selectOptions: response.data.options || {},
        };
    }, [id, isEditMode]);

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);

                const data = await fetchCompanyForm();

                if (!mounted) {
                    return;
                }

                setInitialValues(data.initialValues);
                setSelectOptions(data.selectOptions);
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [fetchCompanyForm]);

    if (loading || !initialValues) {
        return <div>Loading...</div>;
    }

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnChange={false}
            validateOnBlur={true}
        >
            {(formik) => (
                <Container fluid className="d-flex flex-column">
                    <Row className="justify-content-center">
                        <Col sm={12} className="mb-4">
                            <span className="fw-semibold">Company Information</span>
                        </Col>

                        <Col
                            sm="auto"
                            style={{
                                position: 'sticky',
                                top: '10px',
                                height: 'fit-content',
                                alignSelf: 'flex-start',
                                maxWidth: '280px',
                            }}
                        >
                            <Card>
                                <Nav variant="pills" className="flex-column p-3">
                                    {TABS.map((tab) => (
                                        <Nav.Item key={tab.id}>
                                            <Nav.Link
                                                active={activeTab === tab.id}
                                                onClick={() => handleTabChange(tab.id, formik)}
                                                className="mb-2 rounded"
                                            >
                                                {tab.label}
                                            </Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </Nav>
                            </Card>
                        </Col>

                        <Col className="flex-grow-1">
                            <Card className="p-4">
                                <StatusAlert
                                    alert={submitAlert}
                                    onClose={() => setSubmitAlert(null)}
                                />

                                {activeTab === 'basic' && (
                                    <BasicTab
                                        formik={formik}
                                        selectOptions={selectOptions}
                                    />
                                )}

                                {activeTab === 'contract' && (
                                    <ContractTab formik={formik} />
                                )}

                                {activeTab === 'bank' && (
                                    <BankTab
                                        formik={formik}
                                        showTerms={showTerms}
                                        setShowTerms={setShowTerms}
                                    />
                                )}

                                <div className="d-flex justify-content-end mt-3">
                                    {currentIndex < TABS.length - 1 ? (
                                        <Button
                                            type="button"
                                            onClick={() =>
                                                handleTabChange(
                                                    TABS[currentIndex + 1].id,
                                                    formik
                                                )
                                            }
                                            disabled={formik.isSubmitting}
                                        >
                                            Next
                                        </Button>
                                    ) : (
                                        <Button
                                            type="button"
                                            onClick={() => {
                                                if (formik.values.agreeTerms) {
                                                    formik.handleSubmit();
                                                    return;
                                                }

                                                formik.setErrors({
                                                    agreeTerms: 'You must accept terms',
                                                });
                                            }}
                                            disabled={
                                                !formik.values.agreeTerms ||
                                                formik.isSubmitting
                                            }
                                            className="border ms-1 btn-custom-success"
                                        >
                                            Save
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            )}
        </Formik>
    );
};

// Basic Information Tab组件
const BasicTab = ({ formik, selectOptions }) => (
    <FormSection title="Basic Information">
        <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-5">Legal Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="invoiceInfo.legalName" // 匹配 formik.values.supplier.legalName
                        value={formik.values.invoiceInfo.legalName || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.legalName"]}
                        placeholder='Enter your legal name'
                    />
                    <Form.Text className="text-muted">
                        * Must exactly match government-issued business license
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.legalName"]}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
            {/* 其他字段类似，使用name="supplier.fieldName"绑定 */}
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-5">Comercial Name</Form.Label>
                    <Form.Control
                        name="supplier.companyName" // 匹配 formik.values.supplier.legalName
                        value={formik.values.supplier.companyName || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["supplier.companyName"]}
                        placeholder='Enter your comercial name'
                    />
                    <Form.Text className="text-muted">
                        * Display name for invoices and contracts
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["supplier.companyName"]}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
        </Row>
        <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-5">Business Identification Number <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="invoiceInfo.businessIdentNum" // 匹配 formik.values.supplier.legalName
                        value={formik.values.invoiceInfo.businessIdentNum || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.businessIdentNum"]}
                        placeholder='Enter your business identification number'
                    />
                    <Form.Text className="text-muted">
                        * 15/18-digit Unified Social Credit Code or business registration number
                    </Form.Text>
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.businessIdentNum"]}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
            {/* 其他字段类似，使用name="supplier.fieldName"绑定 */}
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-5">Company Web</Form.Label>
                    <Form.Control
                        name="invoiceInfo.webSite" // 匹配 formik.values.supplier.legalName
                        value={formik.values.invoiceInfo.webSite || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.webSite"]}
                        placeholder='Enter your company web address'
                    />
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.webSite"]}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
        </Row>

        <Row>
            <Col md={12}>
                <AddressForm
                    formik={formik}
                    basePath="invoiceInfo"
                    errorInfo={formik.errors['invoiceInfo.street']}
                // language="es"
                // countries="es,us"
                // token={process.env.REACT_APP_MAPBOX_TOKEN}
                />
            </Col>

        </Row>

        <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-5">Fiscal Address Line 2</Form.Label>
                    <Form.Control
                        name="invoiceInfo.houseNumber" // 匹配 formik.values.supplier.legalName
                        value={formik.values.supplier.companyUnitNumber || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.houseNumber"]}
                        placeholder='Enter your unit number'
                    />
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.houseNumber"]}
                    </Form.Control.Feedback>
                </Form.Group>
            </Col>
            {/* 其他字段类似，使用name="supplier.fieldName"绑定 */}
            <Col md={6}>
                <PhoneInput
                    formik={formik}
                    label="Company Phone"
                    prefixField="invoiceInfo.prefix"
                    phoneField="invoiceInfo.phone"
                    error={formik.errors["invoiceInfo.phone"]}
                    placeholder="Enter company phone number"
                />


            </Col>
        </Row>

        <Row>
            <Col md={6}>

                <Form.Group className="mb-3">

                    <Form.Label className="fw-5">Company Email <span className="text-danger">*</span></Form.Label>
                    <InputGroup>
                        <InputGroup.Text>
                            <FaRegEnvelope />
                        </InputGroup.Text>
                        <Form.Control
                            name="invoiceInfo.email" // 匹配 formik.values.supplier.legalName
                            value={formik.values.invoiceInfo.email || ""}
                            onChange={formik.handleChange}
                            isInvalid={!!formik.errors["invoiceInfo.email"]}
                            placeholder='Enter your company email'
                        />
                        <Form.Control.Feedback type="invalid">
                            {formik.errors["invoiceInfo.email"]}
                        </Form.Control.Feedback>
                    </InputGroup>
                </Form.Group>
            </Col>
            {/* 其他字段类似，使用name="supplier.fieldName"绑定 */}
            <Col md={6}>

                <Form.Group className="mb-3">
                    <Form.Label>Language <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                        name="supplier.language"
                        value={formik.values.supplier.language}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                    >
                        {
                            selectOptions.locales && selectOptions.locales.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))
                        }
                    </Form.Select>
                    {/*                         <Form.Text className="text-muted">
                            • System generates invoices on first business day of each month<br />
                            • Requires manual invoice creation
                        </Form.Text> */}
                </Form.Group>
            </Col>

        </Row>

        <Row>
            <Col md={6}>

                <Form.Group className="mb-3">
                    <Form.Label>Currency * <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                        name="supplier.currency"
                        value={formik.values.supplier.currency}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                    >
                        {
                            selectOptions.currencies && selectOptions.currencies.map((option) => (
                                <option key={option.code} value={option.code}>{option.name}</option>
                            ))
                        }
                    </Form.Select>
                    {/*                         <Form.Text className="text-muted">
                            • System generates invoices on first business day of each month<br />
                            • Requires manual invoice creation
                        </Form.Text> */}
                </Form.Group>
            </Col>
            {/* 其他字段类似，使用name="supplier.fieldName"绑定 */}
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label>distance Unit <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                        name="supplier.distanceUnit"
                        value={formik.values.supplier.distanceUnit}
                        onChange={(e) => {
                            formik.handleChange(e);
                        }}
                    >

                        <option value="KM">Kilometers</option>
                        <option value="MI">Miles</option>
                    </Form.Select>
                    {/*                         <Form.Text className="text-muted">
                            • System generates invoices on first business day of each month<br />
                            • Requires manual invoice creation
                        </Form.Text> */}
                </Form.Group>
            </Col>
        </Row>

        {/* 其他字段... */}
    </FormSection >
);


// Contract Conditions Tab组件
const ContractTab = ({ formik }) => {

    return (
        <FormSection title="Contract Conditions">
            <Row>
                <Col md={6}>


                    {/* Prepayment 类型选择 */}
                    <Form.Group className="mb-3">
                        <Form.Label>Upfront Payment Structure</Form.Label>
                        <Form.Select
                            name="rentalDetail.prepaymentType"  // 关键修改：添加 name 属性
                            value={formik.values.rentalDetail.prepaymentType}
                            onChange={(e) => {
                                const v = e.target.value; // "only" | "both"
                                formik.setFieldValue("rentalDetail.prepaymentType", v);
                                if (v === "only") {
                                    formik.setFieldValue("rentalDetail.prepayment", 0);
                                }
                            }}
                        >
                            <option value="only">Platform Fee Only</option>
                            <option value="both">Platform Fee + Operator Upfront</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            {formik.values.rentalDetail?.prepaymentType === false
                                ? "Client pays platform fee now, the rest to operator at pickup"
                                : "Client pays both platform and operator fees upfront"}
                        </Form.Text>
                    </Form.Group>

                    {/* 条件渲染百分比输入框 */}
                    {formik.values.rentalDetail.prepaymentType === "both" && ( // 使用严格等于判断
                        <Form.Group className="mb-3">
                            <Form.Label>Operator Upfront Fee <span className="text-danger">*</span></Form.Label>
                            <InputGroup hasValidation>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="100"
                                    name="rentalDetail.prepayment"
                                    value={formik.values.rentalDetail?.prepayment || ""}
                                    onChange={formik.handleChange}
                                    isInvalid={!!formik.errors["rentalDetail.prepayment"]}
                                    placeholder='Enter operator upfront fee'
                                    aria-describedby="prepayment-percentage-addon"
                                />
                                <InputGroup.Text id="prepayment-percentage-addon">%</InputGroup.Text>
                                <Form.Control.Feedback type="invalid">
                                    {formik.errors["rentalDetail.prepayment"]}
                                </Form.Control.Feedback>
                            </InputGroup>
                            <Form.Text className="text-muted">
                                * Percentage the operator requires upfront
                            </Form.Text>
                        </Form.Group>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Rental Period Calculation</Form.Label>
                        <Form.Select
                            name="rentalDetail.durationType"
                            value={formik.values.rentalDetail.durationType}
                            onChange={(e) => {
                                formik.handleChange(e);
                            }}
                            isInvalid={!!formik.errors.contract?.durationType}
                        >
                            <option value="DAILY">Natural Days</option>
                            <option value="HOURLY">24-hour Periods</option>
                        </Form.Select>
                        <Form.Text className="text-muted mt-1">
                            <small>
                                • Natural Days: Counts calendar days (00:00 pickup day to 00:00 return day)<br />
                                • 24-hour Periods: Each full 24-hour block = 1 day, partial periods count as full day
                            </small>
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Platform Fee<span className="text-danger">*</span><span className="text-success">*</span></Form.Label>

                        <InputGroup hasValidation>
                            <Form.Control
                                type="number"
                                min="0"
                                max="30"
                                name="rentalDetail.commission"
                                value={formik.values.rentalDetail?.commission || ""}
                                onChange={formik.handleChange}
                                isInvalid={!!formik.errors["rentalDetail.commission"]}
                                placeholder='Enter commission'
                                aria-describedby="commission-addon"
                            />
                            <InputGroup.Text id="commission-addon">%</InputGroup.Text>
                            <Form.Control.Feedback type="invalid">
                                {formik.errors["rentalDetail.commission"]}
                            </Form.Control.Feedback>
                        </InputGroup>
                        <Form.Text className="text-muted">
                            RentalMotorbike's service fee percentage
                        </Form.Text>

                    </Form.Group>

                    {/* ✅ Pricing mode select */}
                    <Form.Group className="mb-3">
                        <Form.Label>Pricing mode</Form.Label>
                        <Form.Select
                            name="rentalDetail.pricingMode"
                            value={formik.values.rentalDetail?.pricingMode || "FIXED"}
                            onChange={(e) => formik.setFieldValue("rentalDetail.pricingMode", e.target.value)}
                        >
                            <option value="FIXED">Fixed pricing</option>
                            <option value="SEASON">Seasonal pricing</option>
                        </Form.Select>
                        <Form.Text className="text-muted">
                            Fixed: same price all year. Seasonal: prices vary by season/date range.
                        </Form.Text>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Check
                            id="supplier-isReseller"
                            name="supplier.isReseller"
                            type="switch"
                            label="This supplier is a reseller"
                            checked={Boolean(formik.values.supplier.isReseller)}
                            onChange={(e) =>
                                formik.setFieldValue("supplier.isReseller", e.target.checked)
                            }
                            className="my-2"
                        />
                        <Form.Text className="text-muted">
                            Enable this if the supplier operates as a reseller rather than a direct operator.
                        </Form.Text>
                    </Form.Group>

                    {/* ✅ Taxable switch */}
                    <Form.Group className="mb-3">
                        <Form.Check
                            id="rentalDetail-isTaxable"
                            name="rentalDetail.isTaxable"
                            type="switch"
                            label="Tax applies to bookings"
                            checked={Boolean(formik.values.rentalDetail?.isTaxable)}
                            onChange={(e) => formik.setFieldValue("rentalDetail.isTaxable", e.target.checked)}
                            className="my-2"
                        />
                    </Form.Group>

                </Col>
                {/* 其他合同字段... */}
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Cancellation Policy <span className="text-danger">*</span></Form.Label>
                        <Table bordered>
                            <thead>
                                <tr>
                                    <th className="fw-5">Time Before Pickup</th>
                                    <th className="fw-5">Cancellation Fee (%)</th>
                                    <th className="fw-5">Charge in RMB <span className="text-success">*</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    {
                                        label: "Within 7 days",
                                        refundField: "cancellation7DaysRefund",
                                        chargeField: "chargeCancellationFee7Days",
                                    },
                                    {
                                        label: "Within 15 days",
                                        refundField: "cancellation15DaysRefund",
                                        chargeField: "chargeCancellationFee15Days",
                                    },
                                    {
                                        label: "Within 30 days",
                                        refundField: "cancellation30DaysRefund",
                                        chargeField: "chargeCancellationFee30Days",
                                    },
                                    {
                                        label: "30 days or more",
                                        refundField: "cancellation60DaysRefund",
                                        chargeField: "chargeCancellationFee60Days",
                                    },
                                ].map(({ label, refundField, chargeField }) => (
                                    <tr key={refundField}>
                                        <td>{label}</td>
                                        <td>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    min="0"
                                                    max="100"
                                                    name={`rentalDetail.${refundField}`}
                                                    value={formik.values.rentalDetail?.[refundField] ?? ""}
                                                    isInvalid={!!formik.errors?.rentalDetail?.[refundField]}
                                                    onChange={formik.handleChange}
                                                    aria-describedby={`${refundField}.commission-addon`}
                                                />
                                                <InputGroup.Text id={`${refundField}.commission-addon`}>%</InputGroup.Text>
                                                <Form.Control.Feedback type="invalid">
                                                    {formik.errors?.rentalDetail?.[refundField]}
                                                </Form.Control.Feedback>
                                            </InputGroup>
                                        </td>
                                        <td>
                                            <Form.Group className="mb-0">
                                                <Form.Check
                                                    type="checkbox"
                                                    id={chargeField}
                                                    name={`rentalDetail.${chargeField}`}
                                                    label="Required"
                                                    checked={formik.values.rentalDetail?.[chargeField] ?? false}
                                                    onChange={(e) =>
                                                        formik.setFieldValue(`rentalDetail.${chargeField}`, e.target.checked)
                                                    }
                                                />
                                            </Form.Group>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <Form.Text className="text-muted">
                            The cancellation policy applies based on the time before the scheduled pickup. Please ensure to check the applicable cancellation fee percentage and RMB charge.
                        </Form.Text>
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Label>Status <span className="text-success">*</span></Form.Label>
                        <Form.Select
                            name="supplier.isActive"
                            value={String(formik.values.supplier.isActive)}
                            onChange={(e) => {
                                formik.setFieldValue("supplier.isActive", e.target.value === "true");
                            }}
                        >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Form.Select>
                        {/*                         <Form.Text className="text-muted">
                            • System generates invoices on first business day of each month<br />
                            • Requires manual invoice creation
                        </Form.Text> */}
                    </Form.Group>


                    <Form.Group className="mb-3">
                        <Form.Check
                            id="rentalDetail-api"                 // ★ 必填：让 label 对应到这个 input
                            name="rentalDetail.api"
                            type="switch"
                            label="Use Supplier API"
                            checked={formik.values.rentalDetail.api}
                            onChange={(e) => formik.setFieldValue("rentalDetail.api", e.target.checked)}
                            className="my-2"
                        />
                        {/*                         <Form.Text className="text-muted">
                            • System generates invoices on first business day of each month<br />
                            • Requires manual invoice creation
                        </Form.Text> */}
                    </Form.Group>
                </Col>
            </Row>

        </FormSection>
    );
};

// Bank Details Tab组件
const BankTab = ({ formik, showTerms, setShowTerms }) => (

    // 模态框关闭处理
    <FormSection title="Bank Details">
        <Row>
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label>Account Holder <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="invoiceInfo.accountHolderName"
                        value={formik.values.invoiceInfo?.accountHolderName || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.accountHolderName"]}
                    />
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.accountHolderName"]}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>SWIFT <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="invoiceInfo.swift"
                        value={formik.values.invoiceInfo?.swift || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.swift"]}
                    />
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.swift"]}
                    </Form.Control.Feedback>
                </Form.Group>

            </Col>
            {/* 其他银行字段... */}
            <Col md={6}>
                <Form.Group className="mb-3">
                    <Form.Label>IBAN <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="invoiceInfo.iban"
                        value={formik.values.invoiceInfo?.iban || ""}
                        onChange={formik.handleChange}
                        isInvalid={!!formik.errors["invoiceInfo.iban"]}
                    />
                    <Form.Control.Feedback type="invalid">
                        {formik.errors["invoiceInfo.iban"]}
                    </Form.Control.Feedback>
                </Form.Group>

            </Col>
            {/* 其他银行字段... */}
        </Row>

        <Form.Group controlId="agreeTerms">
            <Form.Check
                type="checkbox"
                label={
                    <>
                        I have read and accept
                        <Button
                            variant="link"
                            className="py-0 ps-1"
                            onClick={() => setShowTerms(true)}
                        >
                            the terms and conditions
                        </Button>
                    </>
                }
                checked={formik.values.agreeTerms}
                onChange={(e) => formik.setFieldValue('agreeTerms', e.target.checked)}
                isInvalid={!!formik.errors.agreeTerms}
            />
        </Form.Group>
        {/* 条款模态框 */}
        <TermsModal
            show={showTerms}
            onClose={() => setShowTerms(false)}
        />

    </FormSection>
);

// 辅助组件
const FormSection = ({ title, children }) => (
    <section>
        <h5 className="pb-3">{title}</h5>
        {children}
    </section>
);

export default CompanyForm;